import React, { useState } from 'react';
import {
  Briefcase,
  Mail,
  Lock,
  ArrowRight,
  ShieldCheck,
  LogOut,
  KeyRound,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { JobMatchLogo } from '../../components/common/JobMatchLogo';
import { ProApplicationWizard } from '../../components/auth/ProApplicationWizard';
import { PhoneOtpVerificationStep } from '../../components/auth/PhoneOtpVerificationStep';
import { GoogleSignInButton } from '../../components/auth/GoogleSignInButton';
import {
  GoogleAuthNoticeBanner,
  GoogleAccountLinkingCard,
  GooglePhoneVerificationCard,
} from '../../components/auth/GoogleAccountSecurityModals';
import { sendTwoFactorCodeApi, supabase } from '../../lib/supabaseService';

export const ProfessionalAuthPage: React.FC = () => {
  const {
    currentUser,
    loginUser,
    logoutUser,
    navigateTo,
    viewParams,
    pendingGoogleAuth,
    initiateGoogleSignIn,
  } = useApp();

  const isCustomerAccount = Boolean(currentUser && currentUser.role === 'customer');
  const [isLogin, setIsLogin] = useState(
    isCustomerAccount
      ? false
      : viewParams.authMode !== 'register' && pendingGoogleAuth?.status !== 'requires_onboarding'
  );
  const [authStep, setAuthStep] = useState<'form' | 'login_2fa' | 'unverified_completion'>('form');
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const [email, setEmail] = useState(isCustomerAccount && currentUser ? currentUser.email : '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [challengeToken, setChallengeToken] = useState<string>('');
  const [unverifiedSessionId, setUnverifiedSessionId] = useState<string>('');
  const [unverifiedActiveStage, setUnverifiedActiveStage] = useState<'email' | 'phone'>('email');
  const [unverifiedPhone, setUnverifiedPhone] = useState<string>('');
  const [unverifiedEmail, setUnverifiedEmail] = useState<string>('');
  const [unverifiedFullName, setUnverifiedFullName] = useState<string>('');
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (viewParams.authMode && !isCustomerAccount) {
      setIsLogin(viewParams.authMode !== 'register');
      setAuthStep('form');
      setError('');
      setSuccessMsg('');
    }
  }, [viewParams.authMode, isCustomerAccount]);

  React.useEffect(() => {
    if (
      pendingGoogleAuth?.status === 'requires_onboarding' &&
      pendingGoogleAuth.requestedRole === 'professional'
    ) {
      setIsLogin(false);
    }
  }, [pendingGoogleAuth]);

  const handleGoogleClick = async (modeOverride?: 'login' | 'register') => {
    setError('');
    setGoogleLoading(true);
    try {
      await initiateGoogleSignIn({
        role: 'professional',
        authMode: modeOverride || (isLogin ? 'login' : 'register'),
        returnView: viewParams.returnView,
      });
    } catch (err: any) {
      setError(err?.message || 'Authentication failed. Could not start Google Sign-In.');
      setGoogleLoading(false);
    }
  };

  // If user is already authenticated as a professional, automatically redirect to dashboard
  React.useEffect(() => {
    if (currentUser && currentUser.role === 'professional') {
      navigateTo(viewParams.returnView || 'pro_dashboard');
    }
  }, [currentUser, navigateTo, viewParams]);

  if (currentUser && currentUser.role === 'professional') {
    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center p-6 text-center space-y-4 max-w-md mx-auto">
        <div className="w-10 h-10 border-3 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
        <p className="text-sm font-semibold text-slate-700">Entering your Professional Dashboard...</p>
      </div>
    );
  }

  // If user is authenticated as a customer and on the Sign In tab of ProfessionalAuthPage, refuse access
  if (currentUser && currentUser.role === 'customer' && isLogin) {
    return (
      <div className="max-w-md mx-auto my-8 sm:my-12 px-4">
        <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-xl text-center space-y-5">
          <div className="flex justify-center mb-2 cursor-pointer" onClick={() => navigateTo('home')}>
            <JobMatchLogo variant="compact" size="lg" />
          </div>
          <div className="w-14 h-14 bg-rose-50 text-rose-600 rounded-2xl flex items-center justify-center mx-auto text-2xl font-bold border border-rose-100 shadow-xs">
            🚫
          </div>
          <div className="space-y-1">
            <h2 className="text-xl font-extrabold text-slate-900">Access Refused: Customer Account</h2>
            <p className="text-xs text-slate-600 leading-relaxed">
              You are currently signed in as a <strong>Customer</strong> (<strong>{currentUser.fullName}</strong> - {currentUser.email}). The Professional sign-in portal is reserved for registered service professionals.
            </p>
            <p className="text-xs font-semibold text-slate-800 mt-2">
              Please go to your Customer Dashboard, switch to the Customer Login page, or submit a Professional Verification Application.
            </p>
          </div>
          <div className="flex flex-col gap-2.5 pt-2">
            <button
              onClick={() => navigateTo('customer_dashboard')}
              className="w-full py-3 px-4 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl text-xs shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer"
            >
              <span>Go to Customer Dashboard</span>
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => setIsLogin(false)}
              className="w-full py-2.5 px-4 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-xl text-xs transition-all flex items-center justify-center gap-2 cursor-pointer"
            >
              <Briefcase className="w-3.5 h-3.5" />
              <span>Apply as a Verified Professional</span>
            </button>
            <button
              onClick={() => logoutUser()}
              className="w-full py-2.5 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl text-xs transition-all flex items-center justify-center gap-2 cursor-pointer"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span>Sign Out &amp; Switch to Professional Account</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');

    if (!email.trim()) {
      setError('Please enter your professional email address or Nigerian phone number.');
      return;
    }
    if (!password) {
      setError('Please enter your password.');
      return;
    }

    setLoading(true);
    try {
      const isEmailInput = email.trim().includes('@');
      const otpRes = await sendTwoFactorCodeApi({
        email: isEmailInput ? email.trim().toLowerCase() : undefined,
        phone: !isEmailInput ? email.trim() : undefined,
        password,
        userType: 'professional',
        purpose: 'login_2fa',
      });
      if (otpRes.success && (otpRes.requiresTwoFactor === false || otpRes.twoFactorVerified)) {
        await handleLogin2faVerified(otpRes);
      } else {
        setChallengeToken('');
        setError(otpRes.message || 'Invalid email or password');
      }
    } catch (err: any) {
      setChallengeToken('');
      setError(err?.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleUnverifiedProCompleted = async (verifyResult?: any) => {
    setLoading(true);
    try {
      const compRes = await fetch('/api/auth/complete-unverified-account', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: verifyResult?.registrationSessionId || unverifiedSessionId,
          verificationProofToken: verifyResult?.verificationProofToken,
          email: unverifiedEmail || email.trim().toLowerCase(),
          phone: unverifiedPhone,
          role: 'professional',
        }),
      });
      const compData = await compRes.json().catch(() => ({}));
      if (!compRes.ok || !compData?.success) {
        throw new Error(compData?.message || compData?.error || 'Could not complete professional verification.');
      }
      await loginUser(email, 'professional', password);
    } catch (err: any) {
      setError(err?.message || 'Failed to complete verification.');
      setAuthStep('form');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin2faVerified = async (verifyResult?: any) => {
    setLoading(true);
    try {
      await loginUser(email, 'professional', password, verifyResult);
    } catch (err: any) {
      setError(err?.message || 'Invalid email or password');
      setChallengeToken('');
      setAuthStep('form');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');

    if (!email.trim()) {
      setError('Please enter your registered professional email address.');
      return;
    }

    setLoading(true);
    try {
      const cleanEmail = email.trim().toLowerCase();
      const isProdHost =
        typeof window !== 'undefined' &&
        (window.location.hostname === 'jobmatch.ng' || window.location.hostname === 'www.jobmatch.ng');
      const redirectUrl = isProdHost
        ? 'https://jobmatch.ng/reset-password'
        : `${window.location.origin}/reset-password`;

      let dispatched = false;
      try {
        const res = await fetch('/api/auth/send-password-reset-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: cleanEmail,
            resetUrl: redirectUrl,
            recipientName: cleanEmail.split('@')[0],
          }),
        });
        const json = await res.json().catch(() => null);
        if (json?.success && json?.dispatchedWithToken) {
          dispatched = true;
        }
      } catch (_) {}

      // Fallback or primary: Supabase official resetPasswordForEmail
      if (!dispatched) {
        await supabase.auth.resetPasswordForEmail(cleanEmail, {
          redirectTo: redirectUrl,
        }).catch(() => {});
      }

      setSuccessMsg('If an account exists with this email, a password reset link has been sent.');
    } catch (_err) {
      setSuccessMsg('If an account exists with this email, a password reset link has been sent.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`${isLogin ? 'max-w-xl' : 'max-w-3xl'} mx-auto my-6 sm:my-10 px-4`}>
      {/* Top Mode Switcher Card */}
      <div className="bg-white rounded-3xl p-5 sm:p-6 border border-slate-200 shadow-sm mb-5">
        <div className="text-center mb-4">
          <div className="flex justify-center mb-3 cursor-pointer" onClick={() => navigateTo('home')}>
            <JobMatchLogo variant="compact" size="lg" />
          </div>
          <h1 className="text-xl sm:text-2xl font-extrabold text-slate-900">
            {isForgotPassword
              ? 'Forgot Your Password?'
              : isLogin
                ? 'Professional Sign In'
                : 'Apply as a Verified JobMatch NG Professional'}
          </h1>
          <p className="text-xs text-slate-500 mt-1 max-w-xl mx-auto">
            {isForgotPassword
              ? "Enter the email address associated with your JobMatch NG account and we'll send a secure reset link."
              : isLogin
                ? 'Sign in to your approved professional account to view matched jobs, submit quotes, and manage payouts.'
                : 'Complete the 7-step professional registration and document verification dossier. Applications are reviewed by our verification team before account activation.'}
          </p>
        </div>

        {!isForgotPassword && (
          <div className="flex rounded-xl bg-slate-100 p-1 text-xs font-semibold max-w-md mx-auto">
            <button
              type="button"
              id="pro-auth-tab-login"
              onClick={() => {
                setIsLogin(true);
                setError('');
                setSuccessMsg('');
              }}
              className={`flex-1 py-2.5 rounded-lg transition-all cursor-pointer ${
                isLogin ? 'bg-white text-slate-900 shadow-xs font-extrabold' : 'text-slate-500 hover:text-slate-900'
              }`}
            >
              Sign In
            </button>
            <button
              type="button"
              id="pro-auth-tab-register"
              onClick={() => {
                setIsLogin(false);
                setError('');
                setSuccessMsg('');
              }}
              className={`flex-1 py-2.5 rounded-lg transition-all cursor-pointer ${
                !isLogin ? 'bg-white text-slate-900 shadow-xs font-extrabold' : 'text-slate-500 hover:text-slate-900'
              }`}
            >
              Register as Professional
            </button>
          </div>
        )}
      </div>

      <GoogleAuthNoticeBanner />
      <GoogleAccountLinkingCard />
      {!isLogin && <GooglePhoneVerificationCard />}

      {/* Render either the 7-Step Email-Only Registration Wizard OR the Sign-In Card */}
      {!isLogin && !isForgotPassword ? (
        <ProApplicationWizard onSwitchToLogin={() => setIsLogin(true)} />
      ) : (
        <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-xl">
          {!isForgotPassword && authStep === 'form' && (
            <div className="mb-6 space-y-4">
              <GoogleSignInButton
                onClick={() => handleGoogleClick('login')}
                loading={googleLoading}
                disabled={loading}
                label="Continue with Google"
              />
              <div className="relative flex items-center justify-center">
                <div className="border-t border-slate-200 w-full" />
                <span className="bg-white px-3 text-[11px] font-semibold text-slate-400 uppercase tracking-wider shrink-0">
                  or sign in with email
                </span>
                <div className="border-t border-slate-200 w-full" />
              </div>
            </div>
          )}
          {error && (
            <div className="p-4 mb-4 rounded-2xl bg-rose-50 border border-rose-200 text-xs text-rose-800 space-y-2">
              <div className="flex items-start gap-2">
                <span className="text-base leading-none">🚫</span>
                <div className="flex-1 font-semibold leading-relaxed">{error}</div>
              </div>
              {error.toLowerCase().includes('customer') && (
                <button
                  type="button"
                  onClick={() => navigateTo('customer_auth', { authMode: 'login' })}
                  className="w-full mt-2 py-2 px-3 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl text-xs shadow-xs transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <span>Go to Customer Login Page</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}

          {successMsg && (
            <div className="p-3 mb-4 rounded-xl bg-emerald-50 border border-emerald-200 text-xs text-emerald-700">
              {successMsg}
            </div>
          )}

          {authStep === 'unverified_completion' ? (
            <PhoneOtpVerificationStep
              email={unverifiedEmail}
              phone={unverifiedPhone}
              fullName={unverifiedFullName}
              userType="professional"
              purpose="registration"
              initialRegistrationSessionId={unverifiedSessionId}
              initialActiveStage={unverifiedActiveStage}
              onVerified={handleUnverifiedProCompleted}
              onBack={() => {
                setAuthStep('form');
              }}
            />
          ) : authStep === 'login_2fa' ? (
            <PhoneOtpVerificationStep
              email={email.includes('@') ? email : undefined}
              phone={!email.includes('@') ? email : undefined}
              userType="professional"
              purpose="login_2fa"
              challengeToken={challengeToken}
              onVerified={handleLogin2faVerified}
              onBack={() => {
                setChallengeToken('');
                setAuthStep('form');
              }}
            />
          ) : isForgotPassword ? (
            <form onSubmit={handleForgotPasswordSubmit} className="space-y-4 text-xs">
              <div>
                <label className="block font-semibold text-slate-700 dark:text-slate-200 mb-1">Registered Email Address</label>
                <div className="relative">
                  <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
                  <input
                    type="email"
                    required
                    placeholder="pro@jobmatch.ng"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-9 pr-3 py-2.5 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white rounded-xl text-sm focus:outline-hidden focus:border-blue-500"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-bold rounded-xl text-sm shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer mt-4"
              >
                {loading ? 'Sending Reset Link...' : 'Send Reset Link'}
              </button>

              <button
                type="button"
                onClick={() => {
                  setIsForgotPassword(false);
                  setError('');
                  setSuccessMsg('');
                }}
                className="w-full py-2.5 px-4 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 font-semibold rounded-xl text-xs transition-all flex items-center justify-center gap-2 cursor-pointer mt-2"
              >
                Back to Sign In
              </button>
            </form>
          ) : (
            <form onSubmit={handleLoginSubmit} className="space-y-4 text-xs">
              <div>
                <label className="block font-semibold text-slate-700 dark:text-slate-200 mb-1">Professional Email</label>
                <div className="relative">
                  <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
                  <input
                    type="email"
                    required
                    placeholder="pro@jobmatch.ng"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-9 pr-3 py-2.5 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white rounded-xl text-sm focus:outline-hidden focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block font-semibold text-slate-700 dark:text-slate-200">Password</label>
                  <button
                    type="button"
                    onClick={() => navigateTo('password_reset', { email: email.trim(), role: 'professional' })}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline font-semibold cursor-pointer flex items-center gap-1"
                  >
                    <KeyRound className="w-3 h-3" />
                    <span>Forgot Password?</span>
                  </button>
                </div>
                <div className="relative">
                  <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
                  <input
                    type="password"
                    required
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-9 pr-3 py-2.5 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white rounded-xl text-sm focus:outline-hidden focus:border-blue-500"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-4 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold rounded-xl text-sm shadow-md shadow-emerald-600/20 transition-all flex items-center justify-center gap-2 cursor-pointer mt-6"
              >
                {loading ? (
                  <span>Authenticating...</span>
                ) : (
                  <>
                    <span>Sign In as Professional</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>

              <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200 text-[11px] text-slate-600 flex items-start gap-2 mt-4">
                <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                <div>
                  <strong>New to JobMatch NG?</strong> Click{' '}
                  <button
                    type="button"
                    onClick={() => setIsLogin(false)}
                    className="text-emerald-700 font-bold underline cursor-pointer"
                  >
                    Register as Professional
                  </button>{' '}
                  above to submit your 7-step application and verification documents for administrator review.
                </div>
              </div>
            </form>
          )}

          {!isForgotPassword && (
            <div className="mt-6 pt-4 border-t border-slate-100 text-center text-xs text-slate-500">
              <span>Need service for your home instead? </span>
              <button
                type="button"
                onClick={() => navigateTo('customer_auth', { authMode: 'register' })}
                className="font-bold text-emerald-700 hover:underline cursor-pointer"
              >
                Sign up as Customer
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
